// CopyWeeklyShift.jsx
import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

import V2PairOrgUnit from "../components/v2_pair_org_unit";
import WeeklyWorkingHoursCompoundSelect from "./weekly_wh_compound_select";
import WeekCalendar from "./week_calendar";

import { createWeeklyWorkingHourCompound } from "../services/api_working_hours";
import { getUiErrorMessage } from "../services/utils";

import { DAYS_LABEL, normalizeBreaks } from "../utils/useWeeklyShiftEditor";

// week_day: 1..7 => "Lunedì".."Domenica"
function weekDayToDayLabel(weekDay) {
  const idx = Number(weekDay);
  if (!idx || idx < 1 || idx > 7) return DAYS_LABEL[0];
  return DAYS_LABEL[idx - 1];
}

function normalizeTimeSlotsFromDay(d) {
  if (Array.isArray(d?.time_slots) && d.time_slots.length > 0) return d.time_slots;
  if (Array.isArray(d?.working_hour?.time_slots) && d.working_hour.time_slots.length > 0) {
    return d.working_hour.time_slots;
  }
  return [];
}

function mapWeeklyCompoundToCalendar(selectedWeekly) {
  const shiftsByDay = {};
  const whNameByDay = {};

  DAYS_LABEL.forEach((d) => (shiftsByDay[d] = []));

  const slotsByWeekday = Array.isArray(selectedWeekly?.slots_by_weekday)
    ? selectedWeekly.slots_by_weekday
    : [];

  slotsByWeekday.forEach((d) => {
    const dayLabel = weekDayToDayLabel(d?.week_day);
    const timeSlots = normalizeTimeSlotsFromDay(d);

    shiftsByDay[dayLabel] = (timeSlots || []).map((s) => ({
      workStartTime: s.start_at,
      workEndTime: s.end_at,
      breaks: normalizeBreaks(s.breaks || []),
    }));

    const whName = d?.working_hour?.name || d?.working_hour_name || "";
    if (whName) whNameByDay[dayLabel] = whName;
  });

  return { daysOfWeek: DAYS_LABEL, shiftsByDay, whNameByDay };
}

function mapWeeklyTemplateToPayloadSlots(selectedWeekly) {
  const src = Array.isArray(selectedWeekly?.slots_by_weekday)
    ? selectedWeekly.slots_by_weekday
    : [];

  return src
    .filter((d) => d && d.week_day)
    .map((d) => {
      const timeSlots = normalizeTimeSlotsFromDay(d);

      return {
        week_day: d.week_day,
        time_slots: (timeSlots || []).map((s) => ({
          start_at: s.start_at,
          end_at: s.end_at,
          breaks: normalizeBreaks(s.breaks || []).map((br) => ({
            start_at: br.start_at,
            end_at: br.end_at,
            is_visible: br.is_visible ?? true,
          })),
        })),
      };
    });
}

export default function CopyWeeklyShift({ token, selectedOu, onSuccess }) {
  const [modelOu, setModelOu] = useState(null); // oggetto OU (o null per generico)
  const [selectedWeekly, setSelectedWeekly] = useState(null);
  const [newShiftName, setNewShiftName] = useState("");

  // reset coerente quando cambia l’origine
  useEffect(() => {
    setSelectedWeekly(null);
  }, [modelOu?.id]);

  const preview = useMemo(() => {
    if (!selectedWeekly) return null;
    return mapWeeklyCompoundToCalendar(selectedWeekly);
  }, [selectedWeekly]);

  const handleCreateWeeklyFromCopy = async () => {
    if (!newShiftName.trim()) {
      return Swal.fire({
        icon: "warning",
        title: "Nome obbligatorio",
        text: "Inserisci il nome del nuovo turno settimanale.",
      });
    }

    if (!selectedWeekly?.slots_by_weekday?.length) {
      return Swal.fire({
        icon: "warning",
        title: "Seleziona un turno",
        text: "Scegli il turno settimanale da copiare.",
      });
    }

    const slotsByWeekday = mapWeeklyTemplateToPayloadSlots(selectedWeekly);
    const hasAnySlots = slotsByWeekday.some((d) => (d.time_slots || []).length > 0);

    if (!hasAnySlots) {
      return Swal.fire({
        icon: "warning",
        title: "Turno vuoto",
        text: "Il turno settimanale selezionato non contiene fasce orarie.",
      });
    }

    const payload = {
      name: newShiftName.trim(),
      ou: selectedOu?.id ?? undefined,
      slots_by_weekday: slotsByWeekday,
    };
    if (!selectedOu?.id) delete payload.ou;

    try {
      Swal.fire({
        title: "Creazione turno settimanale...",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      await createWeeklyWorkingHourCompound(token, payload);

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Turno settimanale creato",
        text: "La copia è stata creata correttamente.",
        confirmButtonText: "OK",
      });

      // reset locale
      setModelOu(null);
      setSelectedWeekly(null);
      setNewShiftName("");

      onSuccess?.();
    } catch (error) {
      Swal.close();
      Swal.fire({ icon: "error", title: "Errore", text: getUiErrorMessage(error) });
    }
  };

  return (
    <div className="mt-4 rounded-b-lg border border-gray-100 bg-gray-100/60 py-4 px-5 space-y-4">
      {/* 1) Da dove copiare */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <V2PairOrgUnit
          label="Scegli l'origine del turno settimanale da copiare"
          helperText="Scegli se copiare da un turno generico oppure da una unità specifica"
          value={modelOu}
          onChange={setModelOu}
          includeGenericOption
          genericLabel="Turni generici (nessuna unità)"
        />
      </div>

      {/* 2) Seleziona turno settimanale da copiare */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold uppercase text-indigo-800">Turno settimanale da copiare</div>
        <div className="text-xs text-gray-500 mt-1">
          Cerca e seleziona il turno settimanale che vuoi usare per la copia
        </div>

        <div className="mt-3">
          <WeeklyWorkingHoursCompoundSelect
            token={token}
            permission="payrolls"
            sourceOu={modelOu}
            value={selectedWeekly}
            onChange={setSelectedWeekly}
          />
        </div>
      </div>

      {/* Preview calendario */}
      {preview && (
        <div className="mt-3">
          <WeekCalendar
            daysOfWeek={preview.daysOfWeek}
            shiftsByDay={preview.shiftsByDay}
            whNameByDay={preview.whNameByDay}
            readOnly
            onPlusCircleClick={() => {}}
            onCancelShift={() => {}}
            onEditDay={() => {}}
            isCustomDayByDay={{}}
          />
        </div>
      )}

      {/* 3) Nome nuovo turno */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold uppercase text-indigo-800">Nome del nuovo turno</div>
        <div className="text-xs text-gray-500 mt-1">* Inserisci il nome</div>

        <div className="mt-3">
          <input
            value={newShiftName}
            onChange={(e) => setNewShiftName(e.target.value)}
            placeholder="Es. Settimana standard - Magazzino"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>
      
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleCreateWeeklyFromCopy}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Crea turno
        </button>
      </div>
    </div>
  );
}